import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';

import { useToast } from '../../../common/hooks/useToast';
import { CrmInvalidId, CrmPageFrame } from '../CrmPageFrame';
import { contactPath, CRM_PATHS, emailCampaignPath, listPath, parseId } from '../crmPaths';
import { count } from '../manage/manage';
import { DeleteDialog, EmptyState, LoadingRows, TABLE_SX } from '../manage/ManageParts';
import { ConfirmDialog } from '../workflows/WorkflowPanels';
import { CampaignEditor } from './CampaignEditor';
import { EmailPreview } from './ComposerParts';
import {
  CRM_CANCEL_EMAIL_CAMPAIGN,
  CRM_DELETE_EMAIL_CAMPAIGN,
  CRM_DUPLICATE_EMAIL_CAMPAIGN,
  CRM_EMAIL_CAMPAIGN,
  CRM_EMAIL_CAMPAIGN_SENDS,
  CRM_UNSCHEDULE_EMAIL_CAMPAIGN,
} from './email.queries';
import { Campaign, describeEmailError, formatRate, isDeletable, isEditable, isMigrated, SEND_META, SendStatus, STATUS_META } from './emailModel';

/** One campaign: the composer while it is a draft of ours; otherwise its figures, the email as sent and its recipients. Plan 4.4. */
export const CrmEmailCampaign = () => {
  const { campaignId: param } = useParams();
  const campaignId = parseId(param);

  if (campaignId === null) {
    return <CrmInvalidId param={param} noun="campaign" listLabel="Email" listPath={CRM_PATHS.email} />;
  }
  return <CampaignPage key={campaignId} campaignId={campaignId} />;
};

const CampaignPage = ({ campaignId }: { campaignId: number }) => {
  const { data, error, refetch } = useQuery<{ crmEmailCampaign: Campaign }>(CRM_EMAIL_CAMPAIGN, {
    variables: { id: campaignId },
    fetchPolicy: 'cache-and-network',
  });
  const campaign = data?.crmEmailCampaign;
  const crumbs = [{ label: 'Email', to: CRM_PATHS.email }, { label: `#${campaignId}` }];

  if (error && !campaign) {
    const gone = error.graphQLErrors?.[0]?.extensions?.code === 'CRM_CAMPAIGN_NOT_FOUND';
    return (
      <CrmPageFrame title={gone ? 'Campaign not found' : `Campaign #${campaignId}`} crumbs={crumbs}>
        <Alert
          severity={gone ? 'warning' : 'error'}
          action={
            gone ? (
              <Button component={RouterLink} to={CRM_PATHS.email} color="inherit" size="small">
                Back to email
              </Button>
            ) : (
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Try again
              </Button>
            )
          }
        >
          {gone ? 'There is no campaign with this id. It may have been deleted.' : describeEmailError(error, 'Could not load the campaign.')}
        </Alert>
      </CrmPageFrame>
    );
  }

  if (!campaign) {
    return (
      <CrmPageFrame title={`Campaign #${campaignId}`} crumbs={crumbs}>
        <Box sx={{ display: 'grid', gap: 2 }} aria-busy>
          <Skeleton variant="rounded" height={90} />
          <Skeleton variant="rounded" height={420} />
        </Box>
      </CrmPageFrame>
    );
  }

  if (isEditable(campaign)) return <CampaignEditor key={campaign.id} campaign={campaign} onChanged={() => refetch()} />;
  return <CampaignReport campaign={campaign} refetch={() => refetch()} />;
};

/* ------------------------------------------------------------------ *
 * The report
 * ------------------------------------------------------------------ */

const fmt = (value: string | null) => (value ? dayjs(value).format('D MMM YYYY, h:mm a') : '');

const CampaignReport = ({ campaign: c, refetch }: { campaign: Campaign; refetch: () => void }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState<'unschedule' | 'cancel' | 'delete' | null>(null);

  const [unschedule, unscheduling] = useMutation(CRM_UNSCHEDULE_EMAIL_CAMPAIGN);
  const [cancel, canceling] = useMutation(CRM_CANCEL_EMAIL_CAMPAIGN);
  const [remove, removing] = useMutation(CRM_DELETE_EMAIL_CAMPAIGN);
  const [duplicate, duplicating] = useMutation(CRM_DUPLICATE_EMAIL_CAMPAIGN);
  const migrated = isMigrated(c);
  const status = STATUS_META[c.status];

  const forget = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmEmailCampaigns' });
    client.cache.gc();
  };

  const copy = async () => {
    try {
      const { data } = await duplicate({ variables: { id: c.id } });
      forget();
      toast.success(`Made ${data.crmDuplicateEmailCampaign.name}, a draft.`);
      navigate(emailCampaignPath(data.crmDuplicateEmailCampaign.id));
    } catch (e) {
      toast.error(describeEmailError(e, 'Could not duplicate it.'));
    }
  };

  const act = async () => {
    try {
      if (confirming === 'unschedule') await unschedule({ variables: { id: c.id } });
      if (confirming === 'cancel') await cancel({ variables: { id: c.id } });
      if (confirming === 'delete') {
        await remove({ variables: { id: c.id } });
        forget();
        toast.success(`Deleted ${c.name}.`);
        navigate(CRM_PATHS.email, { replace: true });
        return;
      }
      forget();
      toast.success(confirming === 'unschedule' ? 'Back to a draft: nothing was sent.' : 'Cancelled. Whatever had not gone will not go.');
      setConfirming(null);
      refetch();
    } catch {
      // Shown in the dialog.
    }
  };

  const running = { unschedule: unscheduling, cancel: canceling, delete: removing };

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {c.status === 'SCHEDULED' && (
        <Button variant="outlined" startIcon={<UndoIcon />} onClick={() => setConfirming('unschedule')}>
          Unschedule
        </Button>
      )}
      {(c.status === 'SCHEDULED' || c.status === 'SENDING') && (
        <Button color="error" startIcon={<CancelOutlinedIcon />} onClick={() => setConfirming('cancel')}>
          Cancel
        </Button>
      )}
      <Button variant={migrated ? 'contained' : 'outlined'} startIcon={duplicating.loading ? <CircularProgress size={14} color="inherit" /> : <ContentCopyIcon />} onClick={copy} disabled={duplicating.loading}>
        Duplicate
      </Button>
      {isDeletable(c) && (
        <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirming('delete')}>
          Delete
        </Button>
      )}
    </Box>
  );

  const sent = c.sentCount;
  const tiles: Array<{ label: string; value: string; sub?: string; tone: string; tip?: string }> = [
    { label: 'Sent', value: count(sent), sub: c.recipientCount != null && c.recipientCount !== sent ? `of ${count(c.recipientCount)} queued` : undefined, tone: '#4f46e5' },
    { label: 'Opened', value: formatRate(c.openRate), sub: `${count(c.openCount)} people`, tone: '#059669', tip: `People, not machines: ${count(c.machineOpenCount)} more opens were mail scanners (Apple Mail privacy and the like), which open everything.` },
    { label: 'Clicked', value: formatRate(c.clickRate), sub: `${count(c.clickCount)} people`, tone: '#0891b2' },
    { label: 'Bounced', value: count(c.bounceCount), tone: '#d97706', tip: 'A hard bounce marks the address bad, so nothing is sent to it again.' },
    { label: 'Unsubscribed', value: count(c.unsubCount), tone: '#64748b' },
    { label: 'Spam reports', value: count(c.spamCount), tone: '#dc2626', tip: 'Each one also unsubscribes the person.' },
  ];

  return (
    <CrmPageFrame title={c.name} crumbs={[{ label: 'Email', to: CRM_PATHS.email }, { label: c.name }]} actions={actions}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        {migrated && (
          <Alert severity="info">
            <AlertTitle>Sent from NationBuilder</AlertTitle>
            Kept with NationBuilder’s own figures. Duplicate it to send it again from here.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={status.label} sx={{ fontWeight: 700, color: status.tone, bgcolor: alpha(status.tone, 0.12) }} />
          {c.workflowStepCount > 0 && <Chip variant="outlined" label="Sent by a workflow" />}
          {c.segmentId && (
            <Typography variant="body2" color="text.secondary">
              To{' '}
              <Link component={RouterLink} to={listPath(c.segmentId)} underline="hover">
                {c.segmentName ?? `list #${c.segmentId}`}
              </Link>
            </Typography>
          )}
        </Box>

        <Timeline campaign={c} />

        {c.status === 'SENDING' && (
          <Box>
            <LinearProgress variant={c.recipientCount ? 'determinate' : 'indeterminate'} value={c.recipientCount ? (sent / c.recipientCount) * 100 : undefined} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary">
              {count(sent)} of {c.recipientCount != null ? count(c.recipientCount) : '…'} sent so far. Reload the page for the latest.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
          {tiles.map((t) => (
            <Tooltip key={t.label} title={t.tip ?? ''} disableHoverListener={!t.tip}>
              <Box sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
                <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: t.tone }} />
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{t.value}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {t.label}
                </Typography>
                {t.sub && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t.sub}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Figures count people, once each, as SendGrid reports them; they keep arriving for days after a send.
        </Typography>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' }, alignItems: 'start' }}>
          <Recipients campaignId={c.id} migrated={migrated} />
          {c.bodyHtml ? (
            <EmailPreview fromName={c.fromName ?? ''} subject={c.subject ?? ''} preheader={c.preheader ?? ''} bodyHtml={c.bodyHtml} />
          ) : (
            <Alert severity="info">{migrated ? 'NationBuilder’s export did not include this email’s body.' : 'This campaign has no HTML body.'}</Alert>
          )}
        </Box>
      </Box>

      {(confirming === 'unschedule' || confirming === 'cancel') && (
        <ConfirmDialog
          title={confirming === 'unschedule' ? `Unschedule ${c.name}?` : `Cancel ${c.name}?`}
          confirm={confirming === 'unschedule' ? 'Unschedule' : 'Cancel it'}
          danger={confirming === 'cancel'}
          running={running[confirming].loading}
          error={running[confirming].error ? describeEmailError(running[confirming].error) : undefined}
          onConfirm={act}
          onClose={() => setConfirming(null)}
        >
          {confirming === 'unschedule' ? (
            <span>It goes back to a draft. Nothing has been sent; you can change it and schedule it again.</span>
          ) : (
            <span>{c.status === 'SENDING' ? `${count(c.sentCount)} have already gone and cannot be called back. ` : ''}Whatever has not gone is marked skipped and never goes. This cannot be undone.</span>
          )}
        </ConfirmDialog>
      )}
      {confirming === 'delete' && (
        <DeleteDialog
          title={`Delete ${c.name}?`}
          confirm="Delete"
          running={removing.loading}
          error={removing.error ? describeEmailError(removing.error) : undefined}
          onConfirm={act}
          onClose={() => setConfirming(null)}
        >
          <span>It never sent to anyone, so no figures are lost.</span>
        </DeleteDialog>
      )}
    </CrmPageFrame>
  );
};

/** Made, scheduled, started, finished (or cancelled): the campaign's life along one line. */
const Timeline = ({ campaign: c }: { campaign: Campaign }) => {
  const steps: Array<{ label: string; at: string | null; done: boolean; bad?: boolean }> = [
    { label: 'Made', at: c.nbCreatedAt ?? c.createdAt, done: true },
    { label: 'Scheduled', at: c.scheduledAt, done: !!c.scheduledAt },
    { label: 'Started', at: c.startedAt, done: !!c.startedAt },
    c.status === 'CANCELED' ? { label: 'Cancelled', at: c.canceledAt, done: true, bad: true } : { label: 'Sent', at: c.sentAt, done: !!c.sentAt },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      {steps.map((s, i) => {
        const tone = s.bad ? '#dc2626' : s.done ? '#4f46e5' : '#cbd5e1';
        return (
          <Box key={s.label} sx={{ position: 'relative', textAlign: 'center', px: 0.5 }}>
            {i > 0 && <Box aria-hidden sx={{ position: 'absolute', top: 7, right: '50%', width: '100%', height: 2, bgcolor: s.done ? tone : '#e2e8f0' }} />}
            <Box aria-hidden sx={{ position: 'relative', mx: 'auto', width: 16, height: 16, borderRadius: '50%', bgcolor: s.done ? tone : '#fff', border: `2px solid ${tone}` }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.75, color: s.done ? 'text.primary' : 'text.disabled' }}>
              {s.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.at && s.done ? fmt(s.at) : s.label === 'Scheduled' && !s.done && c.status === 'SENDING' ? 'Sent now' : '—'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * Who it went to
 * ------------------------------------------------------------------ */

interface Send {
  id: number;
  contactId: number | null;
  contactName: string | null;
  email: string | null;
  status: SendStatus;
  skipReason: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
  spamReportedAt: string | null;
}

const PAGE = 25;

const Recipients = ({ campaignId, migrated }: { campaignId: number; migrated: boolean }) => {
  const [tab, setTab] = useState<SendStatus | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const { data, previousData, loading, error } = useQuery<{ crmEmailCampaignSends: { total: number; items: Array<Send> } }>(CRM_EMAIL_CAMPAIGN_SENDS, {
    variables: { campaignId, status: tab === 'ALL' ? null : tab, skip: page * PAGE, take: PAGE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const shown = (data ?? previousData)?.crmEmailCampaignSends;

  const what = (s: Send) => {
    if (s.spamReportedAt) return { text: 'Reported spam', tone: '#dc2626' };
    if (s.unsubscribedAt) return { text: 'Unsubscribed', tone: '#64748b' };
    if (s.bouncedAt) return { text: 'Bounced', tone: '#d97706' };
    if (s.clickedAt) return { text: 'Clicked', tone: '#0891b2' };
    if (s.openedAt) return { text: 'Opened', tone: '#059669' };
    if (s.deliveredAt) return { text: 'Delivered', tone: '#4f46e5' };
    return null;
  };

  return (
    <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Typography sx={{ fontWeight: 650 }}>Recipients</Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, t) => {
          setTab(t);
          setPage(0);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab value="ALL" label="Everyone" />
        {(['SENT', 'SKIPPED', 'FAILED', 'QUEUED'] as const).map((s) => (
          <Tab key={s} value={s} label={SEND_META[s].label} />
        ))}
      </Tabs>
      {loading && shown ? <LinearProgress sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}
      {error && <Alert severity="error" sx={{ m: 2 }}>{describeEmailError(error, 'Could not load the recipients.')}</Alert>}
      {shown && shown.total === 0 && (
        <EmptyState
          icon={<ContentCopyIcon />}
          title="Nobody here"
          text={migrated ? 'NationBuilder’s export kept the figures, not who each email went to.' : tab === 'ALL' ? 'Nobody has been queued yet: that happens as it starts sending.' : 'No emails are in this state.'}
        />
      )}
      {(!shown || shown.items.length > 0) && (
        <TableContainer sx={TABLE_SX}>
          <Table size="small" aria-label="Recipients">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Since</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!shown && <LoadingRows columns={4} />}
              {shown?.items.map((s) => {
                const latest = what(s);
                const meta = SEND_META[s.status];
                return (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {s.contactId ? (
                        <Link component={RouterLink} to={contactPath(s.contactId)} underline="hover" color="inherit">
                          {s.contactName || `Contact #${s.contactId}`}
                        </Link>
                      ) : (
                        s.contactName ?? '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.email ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip size="small" label={meta.label} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, color: meta.tone, bgcolor: alpha(meta.tone, 0.1) }} />
                      {latest && (
                        <Chip size="small" label={latest.text} sx={{ ml: 0.5, height: 20, fontSize: '0.7rem', color: latest.tone, bgcolor: alpha(latest.tone, 0.08) }} />
                      )}
                      {s.skipReason && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {s.skipReason.toLowerCase().replace(/_/g, ' ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(s.openedAt ?? s.sentAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {shown && shown.total > PAGE && (
        <>
          <Divider />
          <TablePagination component="div" count={shown.total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={PAGE} rowsPerPageOptions={[PAGE]} />
        </>
      )}
    </Box>
  );
};
